/**
 * New auth portal (v3) redirect helpers.
 * Replaces legacy accounts/experience API and token management module for login/logout redirects.
 */

import { resolveAuthPortalReturnUrl } from './authPortalReturnUrl';

/**
 * App URL to use as redirectUrl after login/logout (same logic as landingPage).
 * @returns {string}
 */
export function getAppRedirectUrl() {
  const basePath = (import.meta.env.BASE_URL || "/").replace(/(\/)?$/, "/");
  return (import.meta.env.DEV || window.location.hostname === "localhost")
    ? `${window.location.origin}${basePath}`
    : (process.env.camSdkRedirectUrl || window.location.origin + window.location.pathname);
}

/**
 * Auth portal base host (e.g. auth-qa.netgear.com, auth.netgear.com).
 * Uses process.env.authUrl from Vite define (set per mode in vite.config.mjs). Fallback so logout never gets "undefined" in URL.
 * @returns {string}
 */
export function getAuthPortalHost() {
  const url = process.env.authUrl;
  if (url && url !== 'undefined') return url;
  return "auth-qa.netgear.com";
}

/**
 * Auth portal base URL (e.g. https://auth-qa.netgear.com) for API calls like refresh.
 * @returns {string}
 */
export function getAuthPortalBaseUrl() {
  const host = getAuthPortalHost();
  return host.startsWith("http") ? host : `https://${host}`;
}

/**
 * URL for login page of the new auth portal (with redirect back to app after login).
 * @param {string} [overrideRedirectUrl] - If provided, use this as redirectUrl instead of default app URL (e.g. for redirecting to classic).
 * @returns {string}
*/
export function getAuthPortalLoginUrl(overrideRedirectUrl) {
 const authUrl = getAuthPortalHost();
 const appRedirectUrl = overrideRedirectUrl != null ? overrideRedirectUrl : getAppRedirectUrl();
  return `https://${authUrl}/login?theme=insight&redirectUrl=${encodeURIComponent(appRedirectUrl)}`;
}

/**
 * URL for logout on the OC auth portal. OC clears all cookies and redirects user back to our website.
 * Example: https://auth-qa.netgear.com/logout?redirectUrl=https://demo-aux.insight.netgear.com/
 * Call after Insight logout API and clearing app cookies.
 * @returns {string}
 */
export function getAuthPortalLogoutUrl() {
  const authUrl = getAuthPortalHost();
  const appRedirectUrl = getAppRedirectUrl();
  return `https://${authUrl}/logout?redirectUrl=${encodeURIComponent(appRedirectUrl)}`;
}

/**
 * Redirects to OC auth portal logout. OC clears cookies and redirects user back to our website.
 * Call after (1) Insight logout API and (2) clearing app cookies (e.g. RemoveCookies.removeAccCookies()).
 */
export function redirectToAuthPortalLogout() {
  // Use replace so the current Insight entry is not left in history; otherwise Back can reach
  // Stripe checkout or pre-logout pages after auth portal returns to login.
  window.location.replace(getAuthPortalLogoutUrl());
}

/**
 * Return URL after Update Profile / account settings.
 * Must be the current dashboard page, not the app root: CamSDK at `/` would
 * re-run login and show the account picker again for multi-context managers.
 * `/user-profile*` is itself an AuthPortalRedirect trampoline, so using it
 * as redirectUrl loops back into OC instead of the dashboard.
 */
function getCurrentPageRedirectUrl() {
  if (typeof window === 'undefined') {
    return getAppRedirectUrl();
  }
  return resolveAuthPortalReturnUrl(window.location, sessionStorage) || getAppRedirectUrl();
}

/**
 * URL for account/profile/settings on the auth portal (auth-qa handles 2FA, OTP, update profile).
 * Use this for "Update Profile" and any in-app profile routes; in-app implementation is unused.
 * @param {string} [path='/account'] - Path on auth portal (default /account; change if auth portal uses different path)
 * @returns {string}
 */
export function getAuthPortalAccountUrl(path = '/account') {
  const authUrl = getAuthPortalHost();
  const redirectParam = `redirectUrl=${encodeURIComponent(getCurrentPageRedirectUrl())}`;
  const sep = path.includes('?') ? '&' : '?';
  return `https://${authUrl}${path}${sep}${redirectParam}`;
}

/**
 * Redirects the browser to the auth portal account/settings page.
 * Use for Update Profile and user-profile routes (auth-qa handles these).
 */
export function redirectToAuthPortalAccount() {
  const redirectUrl = getCurrentPageRedirectUrl();
  window.location.href =
    process.env.sdkurl +
    'account/' +
    localStorage.getItem('UserId') +
    '?clientId=' +
    process.env.clientId +
    '&redirectUrl=' +
    encodeURIComponent(redirectUrl);
}
